import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { timeAgo, listingPath } from "@/lib/constants";
import { Check, Trash2, AlertTriangle, Ban, ExternalLink, ArrowUpCircle, BookOpen, ChevronDown, ChevronRight, Star, MessageSquare } from "lucide-react";
import { toast } from "@/lib/toast";
import { RoleTag } from "@/components/admin/RoleTag";
import { AdminDmViewer } from "@/components/admin/AdminDmViewer";
import { DiscussButton } from "@/components/admin/DiscussButton";
import { LoadFailed } from "@/components/LoadState";

const STATUS_TABS = ["open", "escalated", "resolved"];
const TAB_LABEL = { open: "Open", escalated: "Escalated", resolved: "Resolved" };

const ACTIONS = [
  { key: "dismiss", label: "Dismiss", icon: Check, cls: "border-sniper text-secondary-foreground hover:text-foreground" },
  { key: "warn", label: "Warn", icon: AlertTriangle, cls: "border-volt/50 text-volt hover:bg-volt/10" },
  { key: "remove", label: "Remove listing", icon: Trash2, cls: "border-blaze/50 text-blaze hover:bg-blaze/10" },
  { key: "ban", label: "Suspend account", icon: Ban, cls: "border-blaze text-blaze hover:bg-blaze/20" },
  { key: "escalate", label: "Escalate", icon: ArrowUpCircle, cls: "border-amber-500/60 text-amber-400 hover:bg-amber-500/10" },
];
const ACTION_TOAST = {
  dismiss: "Report dismissed.", warn: "User warned.", remove: "Listing removed.",
  ban: "Account suspended & active listings pulled.", escalate: "Escalated for senior review.",
};

function CategoryGuide({ categories }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-sniper bg-surface mb-5" data-testid="moderation-guide">
      <button onClick={() => setOpen((v) => !v)} data-testid="moderation-guide-toggle"
        className="w-full flex items-center gap-2 px-4 py-3 text-left">
        {open ? <ChevronDown className="w-4 h-4 text-olive-bright" /> : <ChevronRight className="w-4 h-4 text-olive-bright" />}
        <BookOpen className="w-4 h-4 text-olive-bright" />
        <span className="font-heading uppercase tracking-tight text-foreground">Category Guide — definitions, examples &amp; recommended action</span>
      </button>
      {open && (
        <div className="border-t border-sniper divide-y divide-sniper">
          {categories.map((c) => (
            <div key={c.id} className="p-4" data-testid={`guide-${c.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-heading uppercase tracking-tight text-foreground">{c.label}</span>
                <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 border border-olive/50 text-olive-bright">applies to: {c.target}</span>
              </div>
              <p className="text-sm font-body text-secondary-foreground mt-1.5">{c.definition}</p>
              <ul className="mt-2 space-y-0.5">
                {c.examples.map((ex, i) => <li key={i} className="text-[12px] font-mono text-muted-foreground">· {ex}</li>)}
              </ul>
              <p className="mt-2 text-[12px] font-mono text-foreground"><span className="text-amber-400 uppercase">Recommended: </span>{c.guidance}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Member-filed report queue (formerly the standalone Moderation page), now a tab inside Content Review.
export default function ReportsPanel({ onChanged }) {
  const [tab, setTab] = useState("open");
  const [reports, setReports] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dmReport, setDmReport] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const catMap = Object.fromEntries(categories.map((c) => [c.id, c]));

  const load = (status) => {
    setLoadErr(null);
    api.get(`/admin/reports?status=${status}`).then(({ data }) => setReports(data.reports))
      .catch((err) => setLoadErr(formatError(err?.response?.data?.detail) || "Couldn't load reports."));
  };

  useEffect(() => {
    load(tab);
    if (categories.length === 0) api.get("/moderation/categories").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
  }, [tab]); // eslint-disable-line

  const act = async (id, action) => {
    if ((action === "ban" || action === "remove") && !window.confirm(`Confirm: ${ACTIONS.find((a) => a.key === action).label} for this report?`)) return;
    try {
      await api.post(`/admin/reports/${id}/action`, { action });
      toast.success(ACTION_TOAST[action]);
      load(tab);
      onChanged?.();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const reclassify = async (id, category) => {
    try {
      await api.post(`/admin/reports/${id}/categorize`, { category });
      toast.success(`Re-classified as ${catMap[category]?.label || category}.`);
      load(tab);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-moderation">
      {categories.length > 0 && <CategoryGuide categories={categories} />}

      <div className="inline-flex border border-sniper bg-surface p-1 mb-5">
        {STATUS_TABS.map((s) => (
          <button key={s} onClick={() => setTab(s)} data-testid={`mod-tab-${s}`}
            className={`px-5 py-2 font-mono uppercase text-xs tracking-wide transition-colors ${tab === s ? "bg-olive text-white" : "text-secondary-foreground hover:text-white"}`}>
            {TAB_LABEL[s]}
          </button>
        ))}
      </div>

      <div className="space-y-3" data-testid="moderation-reports">
        {loadErr && <LoadFailed reason={loadErr} onRetry={() => load(tab)} testid="reports-load-failed" />}
        {!loadErr && reports.length === 0 && <p className="text-sm font-body text-muted-foreground border border-sniper bg-surface p-6">No {TAB_LABEL[tab].toLowerCase()} reports.</p>}
        {!loadErr && reports.map((r) => {
          const cat = r.category ? catMap[r.category] : null;
          const actionable = ["open", "escalated"].includes(r.status);
          return (
            <div key={r.report_id} className="border border-sniper bg-surface p-4" data-testid={`report-${r.report_id}`}>
              <div className="flex items-start gap-4">
                {r.target_kind !== "user" && r.listing_photo && <img src={r.listing_photo} alt="" className="w-16 h-16 object-cover border border-sniper shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {cat ? (
                      <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 border border-blaze/50 text-blaze" data-testid={`report-cat-${r.report_id}`}>{cat.label}</span>
                    ) : (
                      <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 border border-sniper text-muted-foreground">Uncategorized</span>
                    )}
                    {r.target_kind === "user" ? (
                      <>
                      <Link to={`/u/${r.reported_username}`} className="font-heading text-lg text-foreground hover:text-olive-bright inline-flex items-center gap-1">
                        @{r.reported_username} <ExternalLink className="w-3.5 h-3.5" />
                      </Link>
                      <RoleTag kind={r.seller_kind} />
                      </>
                    ) : (
                      <Link to={listingPath(r)} className="font-heading text-lg text-foreground hover:text-olive-bright inline-flex items-center gap-1">
                        {r.listing_title} <ExternalLink className="w-3.5 h-3.5" />
                      </Link>
                    )}
                    {r.target_kind !== "user" && r.listing_status && (
                      <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 border ${r.listing_status === "active" ? "border-olive text-olive-bright" : "border-blaze/50 text-blaze"}`}>{r.listing_status}</span>
                    )}
                    {r.status === "escalated" && <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 border border-amber-500/60 text-amber-400">Escalated</span>}
                  </div>
                  {(r.comment || r.reason) && (
                    <p className="text-xs font-mono text-secondary-foreground mt-1.5">
                      Reporter note: <span className="text-foreground">{r.comment || r.reason}</span>
                    </p>
                  )}
                  {r.source === "circumvention_pattern" && r.meta && (
                    <div className="mt-2 border border-amber-500/40 bg-amber-500/5 px-3 py-2.5" data-testid={`circumvention-flag-${r.report_id}`}>
                      <p className="font-mono text-[10px] uppercase text-amber-400 flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" /> System-detected pattern · review only · no action taken
                      </p>
                      <p className="text-[11px] font-body text-secondary-foreground mt-1">
                        Listing <Link to={listingPath(r)} className="text-foreground hover:text-olive-bright">{r.listing_title}</Link> closed as <span className="text-foreground">{(r.meta.close_reason || "").replace(/_/g, " ")}</span> after messaging {r.meta.engaged?.length} buyer(s):
                      </p>
                      <ul className="mt-1.5 space-y-1">
                        {r.meta.engaged?.map((e) => (
                          <li key={e.conversation_id} className="text-[11px] font-mono text-secondary-foreground flex flex-wrap items-center gap-x-2">
                            <Link to={`/u/${e.buyer_username}`} className="text-olive-bright hover:text-foreground">@{e.buyer_username}</Link>
                            <span>· {e.buyer_msgs} buyer / {e.seller_msgs} seller msgs</span>
                            {e.hours_since_last_msg != null && <span>· closed {e.hours_since_last_msg}h after last message</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1">
                    by {r.reporter_username} <RoleTag kind={r.reporter_kind} /> · {timeAgo(r.created_at)}
                    {r.target_kind !== "user" && r.seller_username && <> · seller: <Link to={`/u/${r.seller_username}`} className="text-secondary-foreground hover:text-foreground">@{r.seller_username}</Link> <RoleTag kind={r.seller_kind} /></>}
                    {r.seller_warnings > 0 && <span className="text-volt"> · {r.seller_warnings} warning(s)</span>}
                    {r.seller_banned && <span className="text-blaze"> · SUSPENDED</span>}
                  </p>
                  {cat && actionable && (
                    <p className="mt-2 text-[12px] font-mono text-foreground border-l-2 border-amber-500/60 pl-2" data-testid={`report-guidance-${r.report_id}`}>
                      <span className="text-amber-400 uppercase">Recommended: </span>{cat.guidance}
                    </p>
                  )}
                  {r.status === "resolved" && (
                    <p className="text-[11px] font-mono uppercase text-olive-bright mt-1" data-testid={`report-resolution-${r.report_id}`}>
                      resolved: {r.resolution} · {timeAgo(r.resolved_at)}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`report-tools-${r.report_id}`}>
                <button onClick={() => setDmReport(r)} data-testid={`view-dm-report-${r.report_id}`}
                  className="inline-flex items-center gap-1.5 border border-olive/50 text-olive-bright hover:bg-olive/10 font-mono uppercase text-xs px-3 py-2 transition-colors">
                  <MessageSquare className="w-3.5 h-3.5" /> View messages
                </button>
                <DiscussButton type="report" id={r.report_id} />
              </div>

              {actionable && (
                <div className="mt-3 pt-3 border-t border-sniper space-y-2.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="font-mono text-[10px] uppercase text-muted-foreground">Category</label>
                    <select value={r.category || ""} onChange={(e) => reclassify(r.report_id, e.target.value)} data-testid={`report-categorize-${r.report_id}`}
                      className="bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground font-mono text-xs transition-colors">
                      <option value="" disabled>Set category…</option>
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {ACTIONS.filter((a) => !(r.target_kind === "user" && a.key === "remove")).filter((a) => !(r.status === "escalated" && a.key === "escalate")).map((a) => {
                      const Icon = a.icon;
                      const recommended = cat && cat.recommended_action === a.key;
                      return (
                        <button key={a.key} onClick={() => act(r.report_id, a.key)} data-testid={`report-${a.key}-${r.report_id}`}
                          className={`relative inline-flex items-center gap-1 border font-heading font-bold uppercase text-sm px-3.5 py-2 transition-colors ${a.cls} ${recommended ? "ring-1 ring-amber-400/70" : ""}`}>
                          {recommended && <Star className="w-3 h-3 text-amber-400 fill-amber-400" />}
                          <Icon className="w-4 h-4" /> {a.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {dmReport && (
        <AdminDmViewer
          caseType="report"
          caseId={dmReport.report_id}
          title={`Report ${dmReport.report_id}${dmReport.reported_username ? ` · @${dmReport.reported_username}` : ""}`}
          open={!!dmReport}
          onClose={() => setDmReport(null)}
        />
      )}
    </div>
  );
}
